import { CARD_SURFACE } from '@/components/ui/section-card-variants'
import { cn } from '@/lib/utils'

/*
 * The dashboard's boxes (f01), shared by the loaded blocks and their
 * skeletons so the two are the same height cell for cell. No components here:
 * the pending page may import it.
 *
 * The counts panel is one filled card of two groups (Signals today, Against
 * their range) with a hairline between them; each group pads itself 20 across
 * and 16 down (16 across on a phone, as the card inset steps down), so the
 * rule runs the panel's full height, as StatStrip's panel rules do. The stat
 * strip under the NEPSE heading is `StatStrip surface="panel"`, not drawn here.
 * The tile rows below are the Signal page's statistics (the dashboard's own figures
 * moved to `StatStrip` and the counts panel above).
 */

/** The card the counts panel and the Signal page's statistics each sit in: the filled card. */
export const ZONE_CARD = CARD_SURFACE

/** Both groups side by side once each has room for its three figures, stacked on a phone. */
export const COUNTS_GRID = 'grid @xl:grid-cols-2'

/**
 * One group: the head row, the figures and the split bar, 12 apart, padded 16 / 24 / 24 across
 * the mock's 18 / 24 / 22 (`szd-sum__half`; 16 across on a phone, as the card inset steps down).
 * The rule is its own.
 */
export const countGroup = (index: number): string =>
  cn(
    'flex min-w-0 flex-col gap-3 border-border-subtle px-6 pt-4 pb-6 @max-sm:px-4',
    index === 0 ? '' : 'border-t @xl:border-t-0 @xl:border-l',
  )

/** The group's head row: the 14/600 title and its total, on the title's 20px line. */
export const COUNT_HEAD = 'flex h-5 items-center justify-between gap-3'

/**
 * The figures in a row, the first at the left and the last at the right, wrapping when it is tight.
 */
export const COUNT_FIGURES = 'flex flex-wrap items-center justify-between gap-x-4 gap-y-3'

/** One figure's line: the 20/28 number and its 12px words, which are the link (S2). */
export const COUNT_FIGURE = 'flex h-7 items-center'

/**
 * A tile row's grid and per-cell rules, by cell count. Only four: the Signal page's statistics
 * and the dashboard's market strip, two by two on a phone, one row from `@2xl`.
 */
const TILE_LAYOUT = {
  4: {
    grid: 'grid-cols-2 @2xl:grid-cols-4',
    rules: [
      '',
      'border-l',
      'border-t @2xl:border-t-0 @2xl:border-l',
      'border-t border-l @2xl:border-t-0',
    ],
  },
} as const

export type TileCount = keyof typeof TILE_LAYOUT

export const tileGrid = (count: TileCount): string => cn('grid', TILE_LAYOUT[count].grid)

/**
 * `flush`: the row's first figure lines up with the column's edge rather than padding in from a
 * card's, the dashboard's market strip (mock `szd-strip2`, no panel: the first cell's left is 0).
 * Below `@2xl` that is every row's first cell.
 */
const FLUSH_START: readonly string[] = ['pl-0 @max-sm:pl-0', '', 'pl-0 @max-sm:pl-0 @2xl:pl-6', '']

/** One tile: the 12/600 label, the 16/600 figure and the 12px line, 4 apart, on its rule. */
export const tileCell = (index: number, count: TileCount, flush = false): string =>
  cn(
    'flex min-w-0 flex-col gap-1 border-border-subtle px-6 py-4 @max-sm:px-4',
    TILE_LAYOUT[count].rules[index],
    flush ? FLUSH_START[index] : null,
  )

/** The NEPSE chart's box: the frame's plot, axis and time labels, 320px. */
export const NEPSE_CHART_HEIGHT = 320
